import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchCustomerDetail } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Card, CardHeader } from '../components/ui/Card';
import { MetricCard } from '../components/ui/MetricCard';
import { Badge } from '../components/ui/Badge';

export function CustomerDetailPage() {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const query = useQuery({ queryKey: ['customerDetail', customerId], queryFn: () => fetchCustomerDetail(customerId), enabled: !!customerId });

  if (query.isLoading) return <LoadingState label="Loading customer…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load this customer.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok || !query.data.customer) {
    return <ErrorState message={query.data?.message || 'This customer could not be found.'} onRetry={() => query.refetch()} />;
  }

  const { customer, records = [], summary } = query.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Customers
        </Link>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white">{customer.name}</h1>
        <p className="mt-1 text-sm text-slate-400">{[customer.phone, customer.email, customer.address].filter(Boolean).join(' • ') || '—'}</p>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="Jobs" value={summary.jobCount} />
          <MetricCard label="Total Quoted" value={summary.totalQuotedText} money />
          <MetricCard label="Total Paid" value={summary.totalPaidText} money />
        </div>
      )}

      <Card>
        <CardHeader title="Jobs" />
        {records.length === 0 ? (
          <EmptyState>No jobs on file for this customer.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {records.map((job) => (
              <li key={job.jobId}>
                <Link
                  to={`/jobs/${encodeURIComponent(job.jobId)}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3.5 transition hover:border-gold-700/60 hover:bg-ink-raised"
                >
                  <span className="truncate text-sm font-semibold text-white">{job.project || job.jobId}</span>
                  <Badge tone="gold" className="shrink-0">
                    {job.stage || job.estimateStatus || '—'}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
